import Box from '@mui/material/Box';
import type { ButtonProps } from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import { usePermissions, usePersonalSettings } from '@tyro/api';
import { Avatar } from '@tyro/core/avatar';
import { usePreferredNameLayout } from '@tyro/core/hooks';
import { iconPickerMap } from '@tyro/core/icon-picker';
import { useTranslation } from '@tyro/i18n';
import { AddIcon, AddNoteIcon, EditIcon } from '@tyro/icons';
import { Link } from '@tyro/router/components';
import {
  type MouseEventHandler,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { useStudentDetailsForNTK } from '../../../api/student/students';
import {
  type NeedToKnowGroup,
  useStudentActions,
} from '../../../store/student-actions-context';
import { getPrimaryContact } from '../../../utils/get-primary-contact';
import {
  getValidNTKQuickActions,
  useNTKQuickActionUtils,
} from './quick-action-utils';

type Action = {
  label: string;
  hasAccess?: boolean;
} & (
  | {
      icon: ReactNode;
      ButtonProps: () => ButtonProps;
      oneClickAction?: boolean;
    }
  | { component: ReactNode }
);

interface NeedToKnowActionsProps {
  studentPartySettings:
    | { partyId: number; group?: NeedToKnowGroup }
    | undefined;
  open: boolean;
  onActionClick?: () => void;
  onActionNavigation?: () => void;
  onActionClose?: () => void;
}

const safeParseActions = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return [];
  }
};

export function NeedToKnowActions({
  studentPartySettings,
  open,
  onActionClick,
  onActionNavigation,
  onActionClose,
}: NeedToKnowActionsProps) {
  const { partyId: studentPartyId = 0, group } = studentPartySettings ?? {};
  const { t } = useTranslation(['common', 'people']);
  const { palette } = useTheme();
  const { displayName } = usePreferredNameLayout();
  const { hasPermission } = usePermissions();
  const [loadingButtons, setLoadingButtons] = useState<Record<number, boolean>>(
    {},
  );
  const {
    getCreateNoteButtonProps,
    getCreateBehaviourButtonProps,
    getAddQuickActionsButtonProps,
  } = useStudentActions();

  const { data: studentData } = useStudentDetailsForNTK(studentPartyId, open);
  const { getNTKQuickActionButtonProps } = useNTKQuickActionUtils();
  const { behaviour_quickActions_list: quickActionsString } =
    usePersonalSettings();

  const primaryContact = useMemo(
    () => getPrimaryContact(studentPartyId, studentData?.contacts),
    [studentPartyId, studentData?.contacts],
  );

  const actions = useMemo<Action[]>(() => {
    const quickActions = getValidNTKQuickActions(
      safeParseActions(quickActionsString),
    ).map<Action>((action) => {
      const Icon = iconPickerMap.get(action.icon)!;

      return {
        label: action.label,
        icon: <Icon />,
        oneClickAction: true,
        ButtonProps: () =>
          getNTKQuickActionButtonProps(action, studentPartyId, group),
      };
    });

    return [
      ...(quickActions.length > 0
        ? quickActions
        : [
            {
              label: t('common:addQuickAction'),
              icon: <AddIcon />,
              ButtonProps: () =>
                getAddQuickActionsButtonProps({
                  onClose: onActionClose,
                  onClick: onActionClick,
                  onNavigate: onActionNavigation,
                }),
            },
          ]),
      {
        label: t('people:addNote'),
        ButtonProps: () =>
          getCreateNoteButtonProps(studentPartyId, {
            onClick: onActionClick,
            onClose: onActionClose,
          }),
        hasAccess: hasPermission('ps:1:notes:write_notes'),
        icon: <EditIcon />,
      },
      {
        label: t('people:addBehaviour'),
        ButtonProps: () =>
          getCreateBehaviourButtonProps(
            {
              defaultStudentPartyIds: [studentPartyId],
              associatedGroups: group ? [group] : [],
            },
            { onClose: onActionClose, onClick: onActionClick },
          ),
        hasAccess: hasPermission('ps:1:notes:write_behaviour'),
        icon: <AddNoteIcon />,
      },
      {
        label: t('common:contact'),
        hasAccess: !!primaryContact,
        component: (
          <Link
            to={`/people/contacts/${primaryContact?.partyId}`}
            onClick={onActionNavigation}
          >
            <Avatar
              src={primaryContact?.person?.avatarUrl}
              person={primaryContact?.person}
              name={displayName(primaryContact?.person)}
              size={52}
            />
          </Link>
        ),
      },
    ].filter((action) => action.hasAccess || action.hasAccess === undefined);
  }, [
    t,
    hasPermission,
    displayName,
    studentPartyId,
    getCreateNoteButtonProps,
    getCreateBehaviourButtonProps,
    primaryContact,
    quickActionsString,
  ]);

  return (
    <>
      <Box className="actions">
        <Box
          sx={{
            '& > div > span': {
              fontWeight: 'medium',
              fontSize: '0.75rem',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            },

            '& .MuiIconButton-root': {
              backgroundColor: alpha(palette.common.white, 0.4),
              backdropFilter: 'blur(16px)',
              transition: 'background-color 0.15s ease-in-out',

              '&:hover': {
                backgroundColor: alpha(palette.common.white, 1),
              },

              '&:not(.MuiIconButton-loading) .MuiSvgIcon-root, & .MuiIconButton-loadingIndicator':
                {
                  color: 'slate.800',
                },

              '& .MuiSvgIcon-root': {
                width: 28,
                height: 28,
              },
            },

            a: {
              textDecoration: 'none',
            },
          }}
        >
          {actions.map((action, index) => {
            const { label } = action;

            if ('component' in action) {
              return (
                <Stack key={label} alignItems="center" spacing={0.5}>
                  {action.component}
                  <span>{label}</span>
                </Stack>
              );
            }

            if ('icon' in action) {
              const { onClick: passedOnClick, ...buttonProps } =
                action.ButtonProps();
              let onClick: MouseEventHandler<HTMLButtonElement> | undefined;

              if (passedOnClick) {
                if (action.oneClickAction) {
                  onClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
                    setLoadingButtons((prev) => ({ ...prev, [index]: true }));
                    await passedOnClick?.(e);
                    setLoadingButtons((prev) => ({ ...prev, [index]: false }));
                  };
                } else {
                  onClick = passedOnClick;
                }
              }

              return (
                <Stack key={label} alignItems="center" spacing={0.5}>
                  <IconButton
                    {...buttonProps}
                    onClick={onClick}
                    size="large"
                    aria-label={label}
                    loading={!!loadingButtons[index]}
                  >
                    {action.icon}
                  </IconButton>
                  <span>{label}</span>
                </Stack>
              );
            }
          })}
        </Box>
      </Box>
    </>
  );
}
